import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '@/api/keys';
import type { MeResponse } from '@/api/types/settings';
import { api, type AuthMode } from '@/lib/api';
import { isInsideShopifyAdmin, tryGetShopifyIdToken } from '@/lib/app-bridge';

type SessionPayload = {
  user: {
    id: number;
    name: string;
    email: string;
    is_shop_access: boolean;
    avatar_url: string | null;
  } | null;
  account: { id: number; name: string } | null;
  merchant: { id: number; shop_domain: string } | null;
};

type AuthState =
  | { status: 'initializing' }
  | { status: 'unauthenticated'; mode: AuthMode }
  | { status: 'authenticated'; mode: AuthMode; session: SessionPayload };

type AuthContextValue = AuthState & {
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
};

export const AuthContext = createContext<AuthContextValue | null>(null);

const EMPTY_SESSION: SessionPayload = { user: null, account: null, merchant: null };

function meToSession(me: MeResponse): SessionPayload {
  return {
    user: me.user,
    account: me.account,
    merchant: me.merchant ? { id: me.merchant.id, shop_domain: me.merchant.shop_domain } : null,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'initializing' });
  const qc = useQueryClient();

  const detectAndLoad = useCallback(async () => {
    setState({ status: 'initializing' });

    if (isInsideShopifyAdmin() && (await tryGetShopifyIdToken())) {
      let session: SessionPayload = EMPTY_SESSION;
      try {
        const me = await api.get<MeResponse>('/admin/me', 'embedded');
        qc.setQueryData(queryKeys.me, me);
        session = meToSession(me);
      } catch {
        // /admin/me failed (transient backend issue, throttled token exchange,
        // etc.). Stay authenticated in embedded mode with a placeholder session
        // so the UI still renders; consumers tolerate null merchant.
      }
      setState({ status: 'authenticated', mode: 'embedded', session });
      return;
    }

    try {
      const session = await api.get<SessionPayload>('/auth/me', 'standalone');
      setState({ status: 'authenticated', mode: 'standalone', session });
    } catch {
      setState({ status: 'unauthenticated', mode: 'standalone' });
    }
  }, [qc]);

  useEffect(() => {
    void detectAndLoad();
  }, [detectAndLoad]);

  const logout = useCallback(async () => {
    if (state.status !== 'authenticated' || state.mode !== 'standalone') {
      return;
    }
    try {
      await api.post<unknown>('/auth/logout', {}, 'standalone');
    } catch {
      // ignore - proceed to unauthenticated regardless
    }
    setState({ status: 'unauthenticated', mode: 'standalone' });
  }, [state]);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, refresh: detectAndLoad, logout }),
    [state, detectAndLoad, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
